import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Tile} from '../../../shared/models/tile';
import {TileValueParser} from '../../../shared/models/tile-value-parser';
import {Schedule, ScheduleDayOfWeek} from '../../../shared/models/schedule';
import {ScheduleEditorComponent} from '../schedule-editor/schedule-editor.component';
import {TilesService} from '../../../core/services/tiles.service';
import {SchedulesService} from '../../../core/services/schedules.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import moment from 'moment';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {PillComponent} from '../pill/pill.component';
import { ObjectType } from 'src/shared/models/object.model';

class ScheduleSetting {
  uuid: string;
  timeOfDay: string = '';
  displayValue: string = '';
  enabled: boolean = true;


  constructor(uuid: string, displayValue: string = null, timeOfDay: string = null, enabled: boolean = true) {
    this.uuid = uuid;
    this.displayValue = displayValue;
    this.timeOfDay = timeOfDay;
    this.enabled = enabled;
  }
}

@Component({
  selector: 'motn-schedules-viewer',
  templateUrl: './schedules-viewer.component.html',
  styleUrls: ['./schedules-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    ScheduleEditorComponent,
    PillComponent,
  ],
})
export class SchedulesViewerComponent implements OnInit {
  @Input() tile: Tile;
  tileValueParser: TileValueParser;

  schedules: Schedule[]; // intentionally not instantiated here

  /**
   * The "mobile" variables are for when the screen width is small enough to
   * trigger responsive stylings
   */
  mobileDayShown: ScheduleDayOfWeek = 0;
  mobileDaysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /**
   * If defined, this will cause the schedule editor to open
   */
  editingSchedule: Schedule;

  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  @ViewChild(ScheduleEditorComponent) scheduleEditor: ScheduleEditorComponent;

  hourLabels: string[] = [
    '<span>Midnight <i class="fa-light fa-moon"></i></span>',
    '1am',
    '2am',
    '3am',
    '4am',
    '5am',
    '6am',
    '7am',
    '8am',
    '9am',
    '10am',
    '11am',
    '<span>Noon <i class="fa-light fa-sun"></i></span>',
    '1pm',
    '2pm',
    '3pm',
    '4pm',
    '5pm',
    '6pm',
    '7pm',
    '8pm',
    '9pm',
    '10pm',
    '11pm',
  ];

  /**
   * Outermost array has 24 elements (hours of day). Next array has 7 elements (days
   * of week). Innermost array has any number of elements, representing schedule
   * settings that fall within a certain hour within a certain day of the week.
   */
  hourCells: Array<Array<Array<ScheduleSetting>>> = [];


  constructor(
    private tilesService: TilesService,
    private schedulesService: SchedulesService,
    private shadeTools: ShadeToolsService,
  ) {

  }


  ngOnInit(): void {
    this.fetchSchedules();
  }


  ngOnDestroy() {
  }


  /**
   *
   * @param render {boolean} Set this to false to not trigger re-rendering of the view
   *  when results are obtained from the API
   * @private
   */
  private fetchSchedules(render = true) {
    this.schedulesService.fetchSchedules$(this.tile.ID).subscribe(
      (schedules) => {
        this.schedules = schedules;
        if (render) {
          this.render();
        }
      },
      () => {
        this.shadeTools.simpleOkDialog('Error', 'Something went wrong when trying to fetch schedules. Try closing and reopening the schedule editor.');
      },
    );
  }


  private render() {
    if (!this.tile || !this.schedules) {
      return;
    }

    this.tileValueParser = new TileValueParser(this.tile);
    this.populateCells();
  }


  private populateCells(): void {
    let settings: ScheduleSetting[];
    let time: moment.Moment;

    this.resetCells();

    // Ensure that each hour cell lists schedules in chronological order
    const sorted = this.schedules.sort((a, b): number => {
      return a.Config.Minute < b.Config.Minute ? -1 : 1;
    });

    for (let schedule of sorted) {
      settings = [];
      time = moment([schedule.Config.Hour, schedule.Config.Minute].join(':'), 'H:m');

      // multiple actions per schedule isn't really a thing yet, but support it here anyway
      for (let action of schedule.Config.Actions) {
        settings.push(new ScheduleSetting(schedule.UUID, this.tileValueParser.getDisplayValue(action.SetValue.Value), time.format('h:mma').replace('m', ''), schedule.Config.Actions[0].SetValue.Enabled));
      }

      for (let dayOfWeek of schedule.Config.DaysOfWeek) {
        this.hourCells[+schedule.Config.Hour][dayOfWeek].push(...settings);
      }
    }
  }


  private resetCells() {
    for (let i = 0; i < 24; i++) {
      this.hourCells[i] = [[], [], [], [], [], [], []];
    }
  }


  addSchedule() {
    this.editingSchedule = {
      ObjectID: this.tile.ID,
      ObjectType: ObjectType.TILE,
      Config: {
        DaysOfWeek: [],
        Hour: 12,
        Minute: 0,
        Actions: [
          {
            SetValue: {
              Enabled: true,
              Value: 0,
            },
          },
        ],
      },
    };
  }


  editSchedule(uuid: string) {
    this.editingSchedule = this.schedules.find((s) => {
      return s.UUID === uuid;
    });
  }


  deleteSchedule(uuid: string) {
    this.schedules = this.schedules.filter((s) => {
      return s.UUID !== uuid;
    });

    this.fetchSchedules();
    this.populateCells();
    this.closeEditor();
  }


  saveEdits(schedule: Schedule) {
    this.fetchSchedules();
    this.populateCells();
    this.closeEditor();
  }


  cancelEdits() {
    this.fetchSchedules();
    this.closeEditor();
  }


  /**
   * Closes the schedule editor
   */
  closeEditor() {
    this.editingSchedule = null;
  }


  mobileShowPreviousDay() {
    this.mobileDayShown--;

    if (this.mobileDayShown < 0) {
      this.mobileDayShown = 6;
    }
  }


  mobileShowNextDay() {
    this.mobileDayShown++;

    if (this.mobileDayShown > 6) {
      this.mobileDayShown = 0;
    }
  }
}
